/*
 * The public channels of this instance, held for a minute (UX §1.5, X-B4 B4-9).
 *
 * Two surfaces need to name a channel the reader is not in: the quick switcher offers it as a row to
 * jump to, and a search hit has to say which channel it was found in even when the reader has never
 * been a member there. Neither can read it from the model — the model holds the conversations the
 * reader is in — and neither may ask the network in the frame it renders in. So the directory is
 * asked for through the browse route (the same action the browse dialog uses, so no new cell), kept
 * here as module state, and answered from memory in between.
 *
 * Asking and reading are therefore two calls and not one: `refreshPublicDirectory` is the ask, and a
 * caller makes it where a side effect belongs — the switcher's provider, which answers inside an
 * effect, or a row's own effect. A reader (`publicChannels`, `publicChannel`) answers out of memory
 * and starts nothing, so a page of search hits cannot start a request from inside a render pass.
 *
 * Everything in this file returns at once and nothing in it throws: a directory nobody asked for is
 * not a failure to report, and both callers have something to show either way — the switcher its rows
 * from the model, the search hit its generic sentence.
 */

import { browseChannels, type ChannelSummary } from "./api.ts"

/** How long the directory is believed. A channel created a minute ago is findable by name. */
const DIRECTORY_TTL_MS = 60_000

let directory: ChannelSummary[] = []
let directoryAt = 0
let inFlight = false

/**
 * Asks for the directory unless a fresh answer is already held or a request is already out. It is
 * safe to call on every keystroke: the TTL, not the caller, decides whether anything happens.
 */
export function refreshPublicDirectory(): void {
	if (inFlight || Date.now() - directoryAt < DIRECTORY_TTL_MS) return
	inFlight = true
	void browseChannels("")
		.then((answer) => {
			directory = answer.channels
			directoryAt = Date.now()
		})
		.catch(() => undefined)
		.finally(() => {
			inFlight = false
		})
}

/**
 * Every public channel this instance has, as the browse route last answered: whatever is held now,
 * which is what lets a caller render in the frame it was typed in. It asks for nothing — the ask is
 * `refreshPublicDirectory`, and a fresh answer lands in a later frame.
 */
export function publicChannels(): readonly ChannelSummary[] {
	return directory
}

/**
 * One public channel, by its slug (with or without the hash a reader writes it with) or by the id of
 * its conversation. `undefined` means "not in the directory we hold", which is also the answer while
 * the first request is still out — so a caller says nothing rather than saying the wrong thing.
 */
export function publicChannel(slugOrId: string | number): ChannelSummary | undefined {
	const held = directory
	if (typeof slugOrId === "number") {
		return held.find((row) => row.conversation.id === slugOrId)
	}
	const slug = slugOrId.startsWith("#") ? slugOrId.slice(1) : slugOrId
	if (slug === "") return undefined
	return held.find((row) => row.conversation.slug === slug)
}

/** Forgets the directory, so the next ask goes to the network. The tests' way back. */
export function forgetPublicChannels(): void {
	directory = []
	directoryAt = 0
}
